import { SALES_ACTIVITY_KINDS, type SalesActivityKind } from '../types/sales-activity';

/** Exhaustive over the API's `kind` enum; an unknown value renders "—", never a guess. */
export const SALES_ACTIVITY_KIND_LABELS: Record<SalesActivityKind, string> = {
  meeting: 'Meeting',
  call: 'Call',
  site_visit: 'Site visit',
  other: 'Other',
};

export function salesActivityKindLabel(kind: unknown): string {
  return typeof kind === 'string' && (SALES_ACTIVITY_KINDS as readonly string[]).includes(kind)
    ? SALES_ACTIVITY_KIND_LABELS[kind as SalesActivityKind]
    : '—';
}

/**
 * CALENDAR-DAY ARITHMETIC, NOT INSTANT ARITHMETIC.
 *
 * The follow-up chips start from the TENANT's today (`todayCalendarDate(tz)`,
 * never the device's and never `toISOString()`, which is UTC's day and is a
 * day late in Alberta after 18:00) and move by whole calendar days. The sum is
 * done on the parts in UTC so no DST hour can move it across midnight.
 */
export function addCalendarDays(day: string, days: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** One calendar month on; the 31st clamps to the next month's last day (Jan 31 → Feb 28). */
export function addCalendarMonths(day: string, months: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1 + months;
  const lastDay = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
  const d = new Date(Date.UTC(y, mo, Math.min(Number(m[3]), lastDay)));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export const FOLLOW_UP_CHIPS = [
  { key: '1w', label: '+1 wk', from: (today: string) => addCalendarDays(today, 7) },
  { key: '2w', label: '+2 wk', from: (today: string) => addCalendarDays(today, 14) },
  { key: '1m', label: '+1 mo', from: (today: string) => addCalendarMonths(today, 1) },
] as const;

/**
 * Who may edit, mark done, or delete an activity: the owner, or admin /
 * office_staff (sales-activities.routes.ts). Every CRM role READS every one.
 */
export function canChangeSalesActivity(
  user: { id?: string | null; role?: string | null } | null | undefined,
  row: { userId?: string | null } | null | undefined,
): boolean {
  if (!user || !row) return false;
  if (user.role === 'admin' || user.role === 'office_staff') return true;
  return !!user.id && user.id === row.userId;
}
